import type { ComputedRef, Ref } from "vue";
import { useInfiniteQuery } from "@tanstack/vue-query";
import type {
  RechercheFacets,
  RechercheFiltresServeur,
  RecherchePage,
} from "~/services/api/rechercheApi";
import {
  fetchCampaignsPage,
  fetchEventsPage,
  fetchAdhesionsPage,
  fetchShopsPage,
  fetchQrCodesPage,
  fetchOrganizationsPage,
} from "~/services/api/rechercheApi";

// Type importé de la SOURCE UNIQUE (`#shared/utils/listingTypes`) plutôt que
// redéclaré : cinq copies de cette union vivaient ici, et l'ajout du rayon
// « boutiques » (2026-08-17) les aurait fait diverger une à une.
import type { ValidType } from "#shared/utils/listingTypes";
// Adresse structurée (lot 4, migration 307) : champs FACULTATIFS — le site
// part avant l'API au déploiement, chaque lecteur tolère leur absence.
import type { ChampsAdresseOrganisation, ChampsLieuStructure } from "@donoor/types";

/**
 * Boutique telle que renvoyée par `/api/public/shops` (rayon + sitemap).
 * Forme volontairement MAIGRE : le rayon n'affiche qu'une carte, il n'a besoin
 * ni des variantes ni du stock.
 */
export interface PublicShop {
  id: string;
  slug: string;
  title: string;
  description?: string | null;
  /** Thème de la boutique (hérite de celui de l'association). */
  category?: string | null;
  cover_image?: string | null;
  organization_id?: number | string;
  /** Nom de l'association — bandeau de la carte, comme cagnottes et événements. */
  organization_name?: string | null;
  /** Logo de l'association — bandeau de la carte. */
  organization_logo?: string | null;
  date_created?: string;
  date_updated?: string;
  products?: Array<{ id: string; name: string }>;
}

export interface PublicCampaign {
  id: number;
  title: string;
  slug: string;
  description: string | null;
  image: string | null;
  goal_amount: number;
  current_amount: number;
  currency: string;
  status: string;
  is_verified?: boolean;
  organization_id: number;
  organization_name: string;
  organization_logo: string | null;
  organization_stripe_connected?: boolean;
  organization_has_official_id?: boolean;
  raised: number;
  donors_count: number;
  percent: number;
  created_at?: string | null;
  category?: string;
}

export interface PublicEvent extends ChampsLieuStructure {
  id: string;
  title: string;
  slug: string;
  cover_image: string | null;
  start_date: string | null;
  location_name: string | null;
  location_city: string | null;
  /** Thème (liste commune, @donoor/utils themes.ts). */
  category: string | null;
  /** Type d'activité (concerts, ateliers…) — migration 301. */
  activity_type?: string | null;
  status: string;
  organization_name: string;
  organization_slug: string;
  organization_logo: string | null;
  min_price?: number | null;
  price_count?: number;
}

export interface PublicPlan {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  cover_image: string | null;
  price: string;
  duration_type: string;
  duration_days?: number | null;
  season_end_date?: string | null;
  category: string | null;
  organization_name: string;
  organization_slug: string;
  organization_logo: string | null;
  min_price?: number;
  price_count?: number;
}

export interface PublicQRCode {
  id: string;
  code: string;
  title: string | null;
  description: string | null;
  organization_id: number;
  organization_name: string;
  organization_logo: string | null;
  organization_address: string | null;
  qr_code_url: string | null;
}

export interface PublicOrganization extends ChampsAdresseOrganisation {
  id: number;
  name: string;
  slug: string;
  logo: string | null;
  cover_image: string | null;
  fallback_banner?: string | null;
  description: string | null;
  objet_social: string | null;
  category: string | null;
  full_address: string | null;
  /** Pays ISO de l'association (migration 298). */
  country?: string | null;
  activities_count: number;
  is_verified?: boolean;
}

// Réponse paginée legacy — conservée pour le contrat de sortie (`*Response`)
// que la page consomme pour distinguer « pas encore loadé » (null) de « vide ».
interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: { total: number; limit: number; offset: number; hasMore: boolean };
}

const ITEMS_PER_PAGE = 30;
const ITEMS_PER_SECTION_TOUT = 6;

/**
 * useRechercheData — charge les 5 collections publiques de /recherche via
 * TanStack `useInfiniteQuery` (migration 2026-08-03, remplace les useLazyFetch
 * maison + l'accumulation manuelle).
 *
 * POURQUOI TanStack : l'ancien mécanisme (watch(pageRef) + rollback d'offset en
 * cas d'erreur) rebouclait sur un 429 → « chargement à l'infini ». useInfiniteQuery
 * gère nativement pages / hasNextPage / fetchNextPage et surtout `retry` borné :
 * une erreur ne peut PLUS déclencher une boucle de re-fetch.
 *
 * CONTRAT PRÉSERVÉ À L'IDENTIQUE (la page + les 4 composables de filtrage ne
 * changent pas) : mêmes refs exposées (`campaigns`… = accumulation aplatie,
 * `*Total`, `*Response` bruts, `infiniteByType`, `isLoading`, `hasError`,
 * `retryActive`, constantes). SEO : en SSR on `await suspense()` les queries
 * actives → la page 1 est rendue dans le HTML serveur (cartes indexables).
 */
export async function useRechercheData(
  activeType: Ref<ValidType> | ComputedRef<ValidType>,
  searchQuery?: Ref<string> | ComputedRef<string>,
  /**
   * Page de départ (1-indexée) des routes `/xxx/page/N`. Décale le premier
   * fetch pour que la page N soit rendue CÔTÉ SERVEUR — sans ça, seuls les 12
   * premiers items existent dans le HTML et Google ne peut atteindre aucune
   * fiche au-delà (mesuré le 2026-08-06 : 12 fiches exposées sur 46, 9 fiches
   * du sitemap jamais crawlées). Le scroll infini reste actif par-dessus.
   */
  startPage?: Ref<number> | ComputedRef<number>,
  /**
   * Filtres du rayon (catégorie, ville, période, prix), appliqués PAR L'API
   * avant pagination (2026-09-26). Ils font partie de la clé TanStack :
   * changer de filtre crée une nouvelle requête, et revenir à un filtre déjà
   * vu ressert son résultat en cache instantanément.
   */
  filtres?: Ref<RechercheFiltresServeur> | ComputedRef<RechercheFiltresServeur>,
) {
  const config = useRuntimeConfig();
  const baseUrl = config.public.apiBaseUrl || "https://api.donoor.org";

  const search = computed(() => (searchQuery?.value || "").trim());

  // Nombre d'items par page : 6 en mode preview « tout », 12 en single-type.
  const perPage = computed(() =>
    activeType.value === "tout" ? ITEMS_PER_SECTION_TOUT : ITEMS_PER_PAGE,
  );

  // Offset du PREMIER fetch. Le mode preview « tout » ne pagine jamais.
  const initialOffset = computed(() => {
    if (activeType.value === "tout") return 0;
    const p = Math.max(1, Math.trunc(startPage?.value ?? 1) || 1);
    return (p - 1) * ITEMS_PER_PAGE;
  });

  // Recherche poussée à l'API en single-type uniquement (le mode « tout » reste
  // une preview ; sa recherche est un filtrage client géré ailleurs).
  const searchParam = computed(() =>
    activeType.value !== "tout" && search.value ? search.value : undefined,
  );

  // Filtres serveur en single-type uniquement (la preview « tout » n'en a pas).
  const filtresParam = computed<RechercheFiltresServeur>(() =>
    activeType.value !== "tout" && filtres ? filtres.value : {},
  );
  /** Forme stable pour la clé de requête (ordre des clés indifférent). */
  const cleFiltres = computed(() =>
    Object.entries(filtresParam.value)
      .filter(([, v]) => v !== undefined && v !== null && v !== "")
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}=${v}`)
      .join("&"),
  );

  // Should-fetch : le type actif (ou « tout ») déclenche le fetch correspondant.
  const shouldFetchCampaigns = computed(() => activeType.value === "tout" || activeType.value === "cagnottes");
  const shouldFetchEvents = computed(() => activeType.value === "tout" || activeType.value === "evenements");
  const shouldFetchAdhesions = computed(() => activeType.value === "tout" || activeType.value === "adhesions");
  const shouldFetchShops = computed(() => activeType.value === "tout" || activeType.value === "boutiques");
  const shouldFetchQRCodes = computed(() => activeType.value === "tout" || activeType.value === "qr-codes");
  const shouldFetchOrganizations = computed(() => activeType.value === "tout" || activeType.value === "associations");

  // ─── Factory d'infinite query ──────────────────────────────────────────
  // getNextPageParam : offset de la page suivante, ou undefined pour STOPPER
  // (dernière page vide = clamp de drift total/base ; ou hasMore=false ; ou en
  // mode « tout » on ne pagine jamais — preview only).
  function makeQuery<T>(
    typeKey: string,
    fetcher: (p: {
      baseUrl: string;
      limit: number;
      offset: number;
      search?: string;
      filtres?: RechercheFiltresServeur;
      signal?: AbortSignal;
    }) => Promise<RecherchePage<T>>,
    enabled: ComputedRef<boolean>,
  ) {
    return useInfiniteQuery({
      // La clé inclut type + search : tout changement crée une NOUVELLE query
      // → repart page 1 (remplacement propre), sans code de reset manuel.
      queryKey: computed(() => [
        "recherche",
        typeKey,
        activeType.value === "tout" ? "preview" : "full",
        searchParam.value ?? "",
        cleFiltres.value,
        // L'offset de départ fait partie de la clé : passer de /cagnottes à
        // /cagnottes/page/3 crée une NOUVELLE query, donc un remplacement
        // propre de la liste — pas une concaténation avec la page précédente.
        initialOffset.value,
      ]),
      initialPageParam: initialOffset.value,
      queryFn: ({ pageParam, signal }) =>
        fetcher({
          baseUrl,
          limit: perPage.value,
          offset: pageParam as number,
          search: searchParam.value,
          filtres: filtresParam.value,
          signal,
        }),
      getNextPageParam: (lastPage: RecherchePage<T>) => {
        // Mode preview « tout » : jamais de page suivante.
        if (activeType.value === "tout") return undefined;
        const { offset, total, limit } = lastPage.pagination;
        const nextOffset = offset + limit;
        // Stop si dernière page vide (drift) ou si on a atteint le total.
        if (lastPage.data.length === 0) return undefined;
        if (nextOffset >= total) return undefined;
        return nextOffset;
      },
      enabled,
      // ⚠️ ANTI-BOUCLE : au plus 1 retry, jamais d'acharnement sur un 429/erreur.
      retry: 1,
      retryDelay: 800,
      // 30 s : revenir sur un filtre déjà vu (« Sport » → « Toutes » →
      // « Sport ») s'affiche instantanément depuis le cache, sans requête.
      // Au-delà, TanStack recharge en arrière-plan (la liste reste affichée).
      staleTime: 30 * 1000,
      // Changement de filtre : la liste actuelle reste affichée pendant le
      // chargement de la nouvelle (pas de squelette qui clignote).
      placeholderData: (previous) => previous,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    });
  }

  const campaignsQuery = makeQuery("campaigns", fetchCampaignsPage, shouldFetchCampaigns);
  const eventsQuery = makeQuery("events", fetchEventsPage, shouldFetchEvents);
  const adhesionsQuery = makeQuery("adhesions", fetchAdhesionsPage, shouldFetchAdhesions);
  const shopsQuery = makeQuery("shops", fetchShopsPage, shouldFetchShops);
  const qrCodesQuery = makeQuery("qrcodes", fetchQrCodesPage, shouldFetchQRCodes);
  const organizationsQuery = makeQuery("organizations", fetchOrganizationsPage, shouldFetchOrganizations);

  // ─── SSR : attendre les queries actives pour garnir le HTML (SEO) ────────
  // suspense() résout la 1re page côté serveur. On n'attend QUE les queries
  // activées (shouldFetch) pour ne pas bloquer sur des collections non affichées.
  if (import.meta.server) {
    const pending: Promise<unknown>[] = [];
    if (shouldFetchCampaigns.value) pending.push(campaignsQuery.suspense());
    if (shouldFetchEvents.value) pending.push(eventsQuery.suspense());
    if (shouldFetchAdhesions.value) pending.push(adhesionsQuery.suspense());
    if (shouldFetchShops.value) pending.push(shopsQuery.suspense());
    if (shouldFetchQRCodes.value) pending.push(qrCodesQuery.suspense());
    if (shouldFetchOrganizations.value) pending.push(organizationsQuery.suspense());
    // allSettled : une collection en erreur ne casse pas le rendu des autres.
    await Promise.allSettled(pending);
  }

  // ─── Accumulation aplatie + dédup par id (contrat: refs T[]) ─────────────
  function flattenDedup<T extends { id: number | string }>(
    pages: RecherchePage<T>[] | undefined,
  ): T[] {
    if (!pages) return [];
    const seen = new Set<number | string>();
    const out: T[] = [];
    for (const page of pages) {
      for (const item of page.data) {
        if (!seen.has(item.id)) {
          seen.add(item.id);
          out.push(item);
        }
      }
    }
    return out;
  }

  const campaigns = computed(() => flattenDedup(campaignsQuery.data.value?.pages));
  const events = computed(() => flattenDedup(eventsQuery.data.value?.pages));
  const adhesions = computed(() => flattenDedup(adhesionsQuery.data.value?.pages));
  const shops = computed(() => flattenDedup(shopsQuery.data.value?.pages));
  const qrCodes = computed(() => flattenDedup(qrCodesQuery.data.value?.pages));
  const organizations = computed(() => flattenDedup(organizationsQuery.data.value?.pages));

  // Totaux = total annoncé par la dernière page reçue (ou 0).
  function lastTotal<T>(pages: RecherchePage<T>[] | undefined): number {
    if (!pages || pages.length === 0) return 0;
    const last = pages[pages.length - 1];
    return last?.pagination.total ?? 0;
  }
  const campaignsTotal = computed(() => lastTotal(campaignsQuery.data.value?.pages));
  const eventsTotal = computed(() => lastTotal(eventsQuery.data.value?.pages));
  const adhesionsTotal = computed(() => lastTotal(adhesionsQuery.data.value?.pages));
  const shopsTotal = computed(() => lastTotal(shopsQuery.data.value?.pages));
  const qrCodesTotal = computed(() => lastTotal(qrCodesQuery.data.value?.pages));
  const organizationsTotal = computed(() => lastTotal(organizationsQuery.data.value?.pages));

  // ─── *Response bruts (contrat : distinguer null = pas loadé de vide) ─────
  // On reconstruit la shape PaginatedResponse legacy depuis la 1re page.
  function firstResponse<T>(
    q: ReturnType<typeof makeQuery<T>>,
  ): ComputedRef<PaginatedResponse<T> | null> {
    return computed(() => {
      const pages = q.data.value?.pages;
      if (!pages || pages.length === 0) return null;
      const first = pages[0];
      if (!first) return null;
      const all = flattenDedup(pages as RecherchePage<T & { id: number | string }>[]);
      return {
        success: true,
        data: all as T[],
        pagination: first.pagination,
      };
    });
  }
  // ─── Facettes du rayon (liste des filtres), lues sur la 1re page ─────────
  function facettes(
    q: { data: { value?: { pages?: RecherchePage<unknown>[] } | undefined } },
  ): ComputedRef<RechercheFacets | null> {
    return computed(() => q.data.value?.pages?.[0]?.facets ?? null);
  }
  const facetsByType: Record<
    "associations" | "cagnottes" | "evenements" | "adhesions",
    ComputedRef<RechercheFacets | null>
  > = {
    associations: facettes(organizationsQuery),
    cagnottes: facettes(campaignsQuery),
    evenements: facettes(eventsQuery),
    adhesions: facettes(adhesionsQuery),
  };
  const facets = computed<RechercheFacets | null>(() => {
    const t = activeType.value;
    return t in facetsByType
      ? facetsByType[t as keyof typeof facetsByType].value
      : null;
  });

  const campaignsResponse = firstResponse(campaignsQuery);
  const eventsResponse = firstResponse(eventsQuery);
  const adhesionsResponse = firstResponse(adhesionsQuery);
  const qrCodesResponse = firstResponse(qrCodesQuery);
  const organizationsResponse = firstResponse(organizationsQuery);

  // ─── Infinite scroll : hasNext / isFetchingNext / fetchNext / nextError ──
  // hasNext = TanStack hasNextPage (piloté par getNextPageParam).
  // isFetchingNext = fetch d'une page ≥ 2 en cours.
  // fetchNext = fetchNextPage (idempotent : no-op si !hasNextPage ou déjà en vol).
  // nextError = une page ≥ 2 a échoué ET on a déjà des items (page 1 OK).
  function makeInfinite<T>(
    q: ReturnType<typeof makeQuery<T>>,
    accu: ComputedRef<T[]>,
  ) {
    const hasNext = computed(() => q.hasNextPage.value ?? false);
    const isFetchingNext = computed(() => q.isFetchingNextPage.value ?? false);
    const nextError = computed(
      () => !!q.error.value && accu.value.length > 0,
    );
    const fetchNext = () => {
      if (q.hasNextPage.value && !q.isFetchingNextPage.value) {
        void q.fetchNextPage();
      }
    };
    return { hasNext, isFetchingNext, fetchNext, nextError };
  }

  const infiniteByType: Record<
    Exclude<ValidType, "tout">,
    {
      hasNext: ComputedRef<boolean>;
      isFetchingNext: ComputedRef<boolean>;
      fetchNext: () => void;
      nextError: ComputedRef<boolean>;
    }
  > = {
    cagnottes: makeInfinite(campaignsQuery, campaigns),
    evenements: makeInfinite(eventsQuery, events),
    adhesions: makeInfinite(adhesionsQuery, adhesions),
    boutiques: makeInfinite(shopsQuery, shops),
    "qr-codes": makeInfinite(qrCodesQuery, qrCodes),
    associations: makeInfinite(organizationsQuery, organizations),
  };

  // ─── isLoading : PAGE 1 en cours (jamais page ≥ 2, sinon flash « vide ») ──
  // isFetching && !isFetchingNextPage = chargement initial (ou refetch page 1).
  const loadingByType: Record<Exclude<ValidType, "tout">, ComputedRef<boolean>> = {
    associations: computed(() => organizationsQuery.isPending.value && shouldFetchOrganizations.value),
    boutiques: computed(() => shopsQuery.isPending.value && shouldFetchShops.value),
    cagnottes: computed(() => campaignsQuery.isPending.value && shouldFetchCampaigns.value),
    evenements: computed(() => eventsQuery.isPending.value && shouldFetchEvents.value),
    adhesions: computed(() => adhesionsQuery.isPending.value && shouldFetchAdhesions.value),
    "qr-codes": computed(() => qrCodesQuery.isPending.value && shouldFetchQRCodes.value),
  };
  const isLoading = computed(() => {
    if (activeType.value === "tout") {
      return (
        (campaignsQuery.isPending.value && shouldFetchCampaigns.value) ||
        (eventsQuery.isPending.value && shouldFetchEvents.value) ||
        (adhesionsQuery.isPending.value && shouldFetchAdhesions.value) ||
        (qrCodesQuery.isPending.value && shouldFetchQRCodes.value) ||
        (organizationsQuery.isPending.value && shouldFetchOrganizations.value)
      );
    }
    return loadingByType[activeType.value]?.value ?? false;
  });

  // ─── hasError : écran pleine page SI page 1 échouée ET accumulation vide ──
  const accumulationByType: Record<Exclude<ValidType, "tout">, ComputedRef<unknown[]>> = {
    associations: organizations,
    boutiques: shops,
    cagnottes: campaigns,
    evenements: events,
    adhesions,
    "qr-codes": qrCodes,
  };
  const errorByType: Record<Exclude<ValidType, "tout">, ComputedRef<unknown>> = {
    associations: computed(() => organizationsQuery.error.value),
    boutiques: computed(() => shopsQuery.error.value),
    cagnottes: computed(() => campaignsQuery.error.value),
    evenements: computed(() => eventsQuery.error.value),
    adhesions: computed(() => adhesionsQuery.error.value),
    "qr-codes": computed(() => qrCodesQuery.error.value),
  };
  const hasError = computed(() => {
    if (activeType.value === "tout") {
      const anyError = !!(
        campaignsQuery.error.value ||
        eventsQuery.error.value ||
        adhesionsQuery.error.value ||
        qrCodesQuery.error.value ||
        organizationsQuery.error.value
      );
      const anyData =
        campaigns.value.length ||
        events.value.length ||
        adhesions.value.length ||
        qrCodes.value.length ||
        organizations.value.length;
      return anyError && !anyData;
    }
    const hasItems = (accumulationByType[activeType.value]?.value.length ?? 0) > 0;
    return !!errorByType[activeType.value]?.value && !hasItems;
  });

  // Relance la page 1 en échec (bouton Réessayer de l'écran pleine page).
  const retryActive = () => {
    if (activeType.value === "tout") {
      if (shouldFetchCampaigns.value) void campaignsQuery.refetch();
      if (shouldFetchEvents.value) void eventsQuery.refetch();
      if (shouldFetchAdhesions.value) void adhesionsQuery.refetch();
      if (shouldFetchQRCodes.value) void qrCodesQuery.refetch();
      if (shouldFetchOrganizations.value) void organizationsQuery.refetch();
      return;
    }
    const byType: Record<Exclude<ValidType, "tout">, () => void> = {
      associations: () => void organizationsQuery.refetch(),
      boutiques: () => void shopsQuery.refetch(),
      cagnottes: () => void campaignsQuery.refetch(),
      evenements: () => void eventsQuery.refetch(),
      adhesions: () => void adhesionsQuery.refetch(),
      "qr-codes": () => void qrCodesQuery.refetch(),
    };
    byType[activeType.value]?.();
  };

  // Barre de chargement Nuxt (la route ne change pas, juste le param `type`).
  // Changement de filtre : l'ancienne liste reste affichée (placeholderData),
  // la barre fine signale que la nouvelle arrive.
  const isChangingFilters = computed(
    () =>
      (organizationsQuery.isPlaceholderData.value && shouldFetchOrganizations.value) ||
      (campaignsQuery.isPlaceholderData.value && shouldFetchCampaigns.value) ||
      (eventsQuery.isPlaceholderData.value && shouldFetchEvents.value) ||
      (adhesionsQuery.isPlaceholderData.value && shouldFetchAdhesions.value),
  );

  if (import.meta.client) {
    const loading = useLoadingIndicator();
    watch(activeType, () => loading.start());
    watch(
      () => isLoading.value || isChangingFilters.value,
      (loadingNow) => {
        if (loadingNow) loading.start();
        else loading.finish();
      },
    );
  }

  return {
    // raw responses (null = pas loadé, sinon data)
    campaignsResponse,
    eventsResponse,
    adhesionsResponse,
    qrCodesResponse,
    organizationsResponse,
    // data (accumulation aplatie)
    campaigns,
    events,
    adhesions,
    qrCodes,
    organizations,
    // totals
    shops,
    shopsTotal,
    campaignsTotal,
    eventsTotal,
    adhesionsTotal,
    qrCodesTotal,
    organizationsTotal,
    // infinite scroll (handles groupés par type actif)
    infiniteByType,
    // contenu de tout le rayon actif (liste des filtres)
    facets,
    // états agrégés
    isLoading,
    hasError,
    retryActive,
    // constantes
    ITEMS_PER_PAGE,
    ITEMS_PER_SECTION_TOUT,
  };
}
